import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { MapPin, DollarSign, Clock, CheckCircle, TrendingUp, Filter, Search, ChevronRight, Building2, Star, Sparkles, Zap, Briefcase } from '../components/CustomIcons';
import EmptyState from '../components/EmptyState';

function JobCard({ job, onView, onApply }) {
  return (
    <div className="card" style={{ padding: '20px', cursor: 'pointer', transition: 'all 0.3s ease' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
      data-testid={`job-card-${job.id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', margin: '0 0 4px 0' }}>{job.title}</h3>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Building2 size={13} />{job.employer}
          </p>
        </div>
        {job.match_score && (
          <div style={{ textAlign: 'center', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '10px', padding: '6px 12px', flexShrink: 0 }}>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '20px', color: '#F97316' }}>{job.match_score}%</div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Match</div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <MapPin size={12} color="#666666" />{job.location}
        </span>
        <span style={{ fontSize: '13px', color: '#22C55E', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <DollarSign size={12} />${job.wage_min}-${job.wage_max}/hr
        </span>
        <span className="badge-grey" style={{ fontSize: '11px' }}>{job.type}</span>
        {job.immediate_hire && <span className="badge-success" style={{ fontSize: '11px' }}>Immediate Hire ✓</span>}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
        {job.second_chance && <span className="badge-orange" style={{ fontSize: '11px' }}>Second-Chance Employer ✓</span>}
        {job.wotc_eligible && <span className="badge-grey" style={{ fontSize: '11px' }}>WOTC Eligible ✓</span>}
      </div>

      {job.required_credentials?.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Required:</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {job.required_credentials.map(c => <span key={c} className="badge-grey" style={{ fontSize: '11px' }}>{c}</span>)}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{job.applicants || 0} applicants</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => onView(job)} className="btn-secondary" style={{ fontSize: '13px', padding: '7px 14px' }}>View Details</button>
          <button onClick={() => onApply(job)} className="btn-primary" style={{ fontSize: '13px', padding: '7px 14px' }} data-testid={`apply-${job.id}`}>Quick Apply</button>
        </div>
      </div>
    </div>
  );
}

function JobDetail({ job, onClose, onApply }) {
  if (!job) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', zIndex: 100, padding: '16px' }} onClick={onClose}>
      <div style={{ background: 'var(--bg-page)', border: '1px solid var(--border-default)', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '560px', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--text-primary)', margin: 0 }}>{job.title}</h2>
          <button onClick={onClose} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '18px' }}>✕</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
          {job.second_chance && <span className="badge-orange" style={{ fontSize: '12px' }}>Second-Chance Employer ✓</span>}
          {job.wotc_eligible && <span className="badge-grey" style={{ fontSize: '12px' }}>WOTC Eligible ✓</span>}
          {job.immediate_hire && <span className="badge-success" style={{ fontSize: '12px' }}>Immediate Hire ✓</span>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '12px' }}>
            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Employer</p>
            <p style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 600 }}>{job.employer}</p>
          </div>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '12px' }}>
            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Location</p>
            <p style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 600 }}>{job.location}</p>
          </div>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '12px' }}>
            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Wage</p>
            <p style={{ fontSize: '14px', color: '#22C55E', fontWeight: 700 }}>${job.wage_min}-${job.wage_max}/hr</p>
          </div>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '12px' }}>
            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>Type</p>
            <p style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 600 }}>{job.type}</p>
          </div>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '10px' }}>About This Role</h4>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.7 }}>{job.description}</p>
        </div>
        {job.match_score && (
          <div style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
            <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '22px', color: '#F97316', margin: '0 0 4px 0' }}>Your Match: {job.match_score}%</p>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>Based on your earned credentials and pathway progress</p>
          </div>
        )}
        <button onClick={() => onApply(job)} className="btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: '15px', padding: '14px' }} data-testid="job-detail-apply-btn">
          Apply with Credentials
        </button>
      </div>
    </div>
  );
}

export default function Jobs() {
  const { authFetch, user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sector, setSector] = useState('All');
  const [selectedJob, setSelectedJob] = useState(null);
  const [applying, setApplying] = useState(null);
  const [applied, setApplied] = useState(new Set());
  const isLearner = user?.role === 'learner';

  const SECTORS = ['All', 'Skilled Trades', 'Logistics', 'Technology', 'Healthcare', 'Food Service'];

  useEffect(() => {
    const base = authFetch('/jobs').then(r => { setJobs(r.data); setFiltered(r.data); }).catch(() => {}).finally(() => setLoading(false));
    if (isLearner) {
      authFetch('/jobs/recommended').then(r => setRecommended(r.data || [])).catch(() => {});
    }
    return () => {};
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let results = jobs;
    if (sector !== 'All') results = results.filter(j => j.sector === sector);
    if (search) results = results.filter(j => j.title.toLowerCase().includes(search.toLowerCase()) || j.employer.toLowerCase().includes(search.toLowerCase()));
    setFiltered(results);
  }, [sector, search, jobs]);

  const handleApply = async (job) => {
    if (applied.has(job.id)) return;
    setApplying(job.id);
    try {
      await authFetch(`/jobs/${job.id}/apply`, { method: 'post' });
      setApplied(prev => new Set([...prev, job.id]));
      setSelectedJob(null);
    } catch (e) { console.error('apply-job error:', e); }
    setApplying(null);
  };

  return (
    <div className="page-wrapper" style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="jobs-page">
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--text-primary)', margin: '0 0 6px 0' }}>Second-Chance Careers</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>Employers who value your skills, not your past.</p>
        </div>

        {/* Jobs For You — personalized recommendations */}
        {isLearner && recommended.length > 0 && (
          <div style={{ marginBottom: '28px' }} data-testid="jobs-for-you-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <Sparkles size={16} style={{ color: 'var(--orange)' }} aria-hidden="true" />
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '16px', color: 'var(--text-primary)', margin: 0 }}>
                Jobs For You
              </h2>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', padding: '2px 8px', borderRadius: '10px', background: 'var(--bg-elevated)', border: '1px solid var(--border-default)' }}>
                Based on your credentials
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
              {recommended.slice(0, 3).map(job => (
                <div
                  key={job.id}
                  data-testid={`recommended-job-${job.id}`}
                  onClick={() => setSelectedJob(job)}
                  style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid rgba(249,115,22,0.25)',
                    borderRadius: '14px',
                    padding: '18px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(249,115,22,0.6)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(249,115,22,0.25)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {/* Glow strip */}
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, var(--orange), #F59E0B)' }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div style={{ flex: 1, marginRight: '12px' }}>
                      <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)', margin: '0 0 3px' }}>{job.title}</h3>
                      <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>{job.employer}</p>
                    </div>
                    <div style={{ textAlign: 'center', background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: '10px', padding: '5px 10px', flexShrink: 0 }}>
                      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '18px', color: 'var(--orange)', lineHeight: 1 }}>{job.match_score}%</div>
                      <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>Match</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: '#22C55E', fontWeight: 600 }}>${job.wage_min}–${job.wage_max}/hr</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{job.location}</span>
                  </div>

                  {/* Why matched reasons */}
                  {job.why_matched?.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
                      {job.why_matched.map((reason, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Zap size={10} style={{ color: 'var(--orange)', flexShrink: 0 }} aria-hidden="true" />
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{reason}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={e => { e.stopPropagation(); handleApply(job); }}
                    data-testid={`recommended-apply-${job.id}`}
                    aria-label={`Quick apply to ${job.title} at ${job.employer}`}
                    disabled={applied.has(job.id)}
                    style={{
                      width: '100%', padding: '8px', borderRadius: '8px', border: 'none',
                      background: applied.has(job.id) ? '#22C55E' : 'var(--orange)',
                      color: '#111111', fontSize: '12px', fontWeight: 700,
                      fontFamily: 'Plus Jakarta Sans, sans-serif', cursor: applied.has(job.id) ? 'default' : 'pointer',
                    }}>
                    {applied.has(job.id) ? 'Applied ✓' : 'Quick Apply'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search jobs, employers..."
                aria-label="Search jobs and employers"
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '10px', color: 'var(--text-primary)', padding: '10px 12px 10px 38px', width: '100%', fontFamily: 'Inter, sans-serif', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = 'var(--orange)'} onBlur={e => e.target.style.borderColor = 'var(--border-default)'}
                data-testid="jobs-search" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {SECTORS.map(s => (
              <button key={s} onClick={() => setSector(s)}
                style={{ padding: '6px 14px', borderRadius: '20px', border: sector === s ? '1px solid var(--orange)' : '1px solid var(--border)', background: sector === s ? 'var(--orange-subtle)' : 'transparent', color: sector === s ? 'var(--orange-text)' : 'var(--text-muted)', fontSize: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>{filtered.length} positions available</p>
          <span className="badge-success" style={{ fontSize: '11px' }}>All second-chance employers</span>
        </div>

        {loading ? (
          <div style={{ display: 'grid', gap: '16px' }}>
            {[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ height: '180px', borderRadius: '12px' }} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ background: 'var(--bg-elevated)', borderRadius: '12px', border: '1px solid var(--border-default)' }}>
            <EmptyState
              icon={Briefcase}
              title="No job matches yet"
              subtitle="Earn more credentials to unlock employer matches in your sector."
              actionLabel="View Pathways"
              actionPath="/learn"
            />
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {filtered.map(job => (
              <div key={job.id} style={{ position: 'relative' }}>
                {applied.has(job.id) && (
                  <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10, background: 'var(--green)', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#ffffff', fontWeight: 700 }}>Applied ✓</div>
                )}
                <JobCard job={job} onView={setSelectedJob} onApply={handleApply} />
              </div>
            ))}
          </div>
        )}
      </div>
      <JobDetail job={selectedJob} onClose={() => setSelectedJob(null)} onApply={handleApply} />
    </div>
  );
}
